import { cn } from "@-/com.npmjs.cn";
import { impactAsync, ImpactFeedbackStyle, selectionAsync } from "@-/com.npmjs.expo-haptics";
import { PanResponder, Platform, Pressable, View } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { Gesture, GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import Animated, {
  cancelAnimation,
  runOnJS,
  runOnUI,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { PeriodNavigationControls } from "@-/dev.improve.haniverse.components.ui.period-navigation";
import { Surface, SurfaceHeader } from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { localDayKey } from "@-/dev.improve.haniverse.lib.time.local-day";
import {
  USER_CARD_STAGE_LABEL,
  USER_CARD_STAGE_ORDER,
  type UserCardStage,
} from "@-/dev.improve.haniverse.constants.user-card-stage";
import useLatest from "@-/com.npmjs.use-latest";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  deckUpcomingHourLabel,
  deckUpcomingChartRangeLabel,
  isDeckUpcomingPeriodSelected,
  shiftDeckUpcomingRange,
  type DeckUpcomingPeriod,
  type DeckUpcomingTimeframe,
} from "#build-deck-upcoming-schedule";

const TIMEFRAMES = ["day", "week", "month"] as const;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const PAN_SPRING = { damping: 28, mass: 0.8, stiffness: 260 } as const;
const ZOOM_TIMING = { duration: 260 } as const;
/** Holding a bar this long starts selecting several bars; dragging then extends the selection. */
const MULTI_SELECT_LONG_PRESS_MS = 400;
/** Horizontal travel along the date axis that doubles (right) or halves (left) the visible span. */
const AXIS_ZOOM_DOUBLING_DISTANCE = 120;
/**
 * Bars and axis labels are laid out in percentages of a track one day wide, whose left edge is a
 * fixed instant (the chart's first window start). React only decides which bars exist; where the
 * track sits and how wide a day is come from shared values alone. A render that moves the window
 * therefore never moves a bar that stays on screen, whichever of React's commit and the UI
 * thread's viewport update reaches the screen first.
 */
const TRACK_UNIT_MS = DAY_MS;
type SelectedSpan = Pick<DeckUpcomingPeriod, "date" | "endDate" | "hour">;
/** The snapped window the viewport rests on. A released pan sets it on the UI thread. */
type ChartWindowState = {
  count: number;
  span: number;
  start: number;
  timeframe: DeckUpcomingTimeframe;
};
/** A zoom crossfade: each layer fades from the opacity it had when the zoom began. */
type ChartFade = {
  from: DeckUpcomingTimeframe | null;
  fromOpacity: number;
  to: DeckUpcomingTimeframe;
  toOpacity: number;
};

/** A zoom gesture's scale, limited to one neighboring timeframe in either direction. */
function clampZoomScale(timeframe: DeckUpcomingTimeframe, scale: number) {
  "worklet";
  // One adjacent window on either side keeps every focal point covered while zooming out.
  return Math.max(timeframe === "month" ? 1 : 0.5, Math.min(timeframe === "day" ? 1 : 3, scale));
}

/** Whether a finished zoom gesture steps in to a narrower timeframe (-1), out (1), or back (0). */
function zoomGestureStep(timeframe: DeckUpcomingTimeframe, scale: number) {
  "worklet";
  return timeframe !== "day" && scale > 1.25 ? -1 : timeframe !== "month" && scale < 0.8 ? 1 : 0;
}

/**
 * Whether a finger's travel from touch-down makes a horizontal swipe: past 8pt, and mostly
 * sideways so vertical drags are left alone.
 */
function isHorizontalSwipe(dx: number, dy: number) {
  "worklet";
  return Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.5;
}

/** A timeframe layer's opacity during (and after) a zoom crossfade; other layers are hidden. */
function chartLayerOpacity(layer: DeckUpcomingTimeframe, fade: ChartFade, blend: number) {
  "worklet";
  return layer === fade.to
    ? fade.toOpacity + (1 - fade.toOpacity) * blend
    : layer === fade.from
      ? fade.fromOpacity * (1 - blend)
      : 0;
}

/** A duration as a percentage of the track's width (see `TRACK_UNIT_MS`). */
function trackPercent(durationMs: number) {
  return `${(durationMs / TRACK_UNIT_MS) * 100}%` as const;
}

/** Haptics are a nicety: an unavailable engine (web, tests, an old device) is never an error. */
function playMultiSelectFeedback(feedback: "start" | "extend") {
  if (Platform.OS === "web") {
    return;
  }
  (feedback === "start" ? impactAsync(ImpactFeedbackStyle.Medium) : selectionAsync()).catch(
    () => {},
  );
}
/**
 * Fixed chart chrome outside the measured header and controls: the frame's bottom padding (4),
 * the surface's top and bottom borders (2) and the body's top padding (12).
 */
const CHART_FRAME_HEIGHT = 4 + 2 + 12;
type ChartWindow = {
  adjacentPeriods?: { after: readonly DeckUpcomingPeriod[]; before: readonly DeckUpcomingPeriod[] };
  periods: readonly DeckUpcomingPeriod[];
  rangeStartTime: number;
  span: number;
  timeframe: DeckUpcomingTimeframe;
};
/**
 * The windows drawn: the current one, the timeframe a zoom is fading out of, and the bars of a
 * window jumped away from (see `nextChartLayers`).
 */
type ChartLayers = {
  current: ChartWindow;
  outgoing: ChartWindow | null;
  retained: readonly DeckUpcomingPeriod[] | null;
};
const STAGE_BAR_SEGMENT_CLASS_NAME: Record<UserCardStage, string> = {
  familiar: "bg-stage-familiar",
  mastered: "bg-stage-mastered",
  new: "bg-stage-new",
  proficient: "bg-stage-proficient",
};

function chartWindowPeriods(window: ChartWindow) {
  return [
    ...(window.adjacentPeriods?.before ?? []),
    ...window.periods,
    ...(window.adjacentPeriods?.after ?? []),
  ];
}

function nextChartLayers(layers: ChartLayers, next: ChartWindow): ChartLayers {
  const previous = layers.current;
  if (previous.timeframe !== next.timeframe) {
    // A zoom fades the timeframe being left out over the new one; reversing it re-promotes it.
    return { current: next, outgoing: previous, retained: null };
  }
  if (previous.rangeStartTime === next.rangeStartTime && previous.span === next.span) {
    return { ...layers, current: next };
  }
  const covered = chartWindowPeriods(next);
  const coveredStart = covered[0]?.date.getTime();
  const coveredEnd = covered.at(-1)?.endDate.getTime();
  return {
    current: next,
    outgoing: layers.outgoing,
    // Pans and single steps stay within the adjacent windows. A longer jump (Today) keeps drawing
    // the bars it left until the UI thread moves the viewport, so no frame shows an empty plot.
    retained:
      coveredStart !== undefined &&
      coveredEnd !== undefined &&
      coveredStart <= previous.rangeStartTime &&
      previous.rangeStartTime + previous.span <= coveredEnd
        ? null
        : previous.periods,
  };
}

export function DeckUpcomingReviewChart({
  adjacentPeriods,
  isTodayInView,
  loading = false,
  maximumCardCount,
  onControlsHeightChange,
  onNavigate,
  onPan,
  onSelect,
  onSelectSpan,
  onTimeframeChange,
  onToday,
  overdueCount,
  periods,
  rangeEnd,
  rangeStart,
  selectedKey,
  selectedSpan,
  timeframe,
  todayKey,
  unscheduledCount,
}: {
  adjacentPeriods?: { after: readonly DeckUpcomingPeriod[]; before: readonly DeckUpcomingPeriod[] };
  /** Whether the visible window overlaps today; Today in the header fills in while it does not. */
  isTodayInView: boolean;
  loading?: boolean;
  maximumCardCount: number;
  /** Reports the height of everything but the plot, which the schedule layout keeps on screen. */
  onControlsHeightChange: (height: number) => void;
  onNavigate: (direction: number) => void;
  /** A released pan settled on the window of `periodCount` bars starting at `rangeStartTime`. */
  onPan: (rangeStartTime: number, periodCount: number) => void;
  onSelect: (key: string, toggle?: boolean) => void;
  /** Selects every bar from the held bar to the bar under the finger, in either order. */
  onSelectSpan: (fromKey: string, toKey: string) => void;
  onTimeframeChange: (timeframe: DeckUpcomingTimeframe) => void;
  onToday: () => void;
  overdueCount: number;
  periods: readonly DeckUpcomingPeriod[];
  rangeEnd: Date;
  rangeStart: Date;
  selectedKey: string;
  /** The explicitly selected bars; bars of another size (days versus hours) stay unselected. */
  selectedSpan?: SelectedSpan;
  timeframe: DeckUpcomingTimeframe;
  todayKey: string;
  unscheduledCount: number;
}) {
  const allPeriods = [
    ...(adjacentPeriods?.before ?? []),
    ...periods,
    ...(adjacentPeriods?.after ?? []),
  ];
  const rangeStartTime = rangeStart.getTime();
  const rangeSpan = rangeEnd.getTime() - rangeStartTime;
  const periodCount = periods.length;
  const chartWindow = useMemo(
    () => ({ adjacentPeriods, periods, rangeStartTime, span: rangeSpan, timeframe }),
    [adjacentPeriods, periods, rangeStartTime, rangeSpan, timeframe],
  );
  // Derived while rendering, so the commit that changes the window already holds every layer.
  const [layers, setLayers] = useState<ChartLayers>(() => ({
    current: chartWindow,
    outgoing: null,
    retained: null,
  }));
  if (layers.current !== chartWindow) {
    setLayers(nextChartLayers(layers, chartWindow));
  }
  const [trackOrigin] = useState(rangeStartTime);
  const rangeLabel = deckUpcomingChartRangeLabel(rangeStart, rangeEnd, timeframe);
  const [plotWidth, setPlotWidth] = useState(0);
  const chromeHeights = useRef({ controls: 0, header: 0 });
  const reportChromeHeight = (part: "controls" | "header", height: number) => {
    chromeHeights.current[part] = height;
    onControlsHeightChange(
      CHART_FRAME_HEIGHT + chromeHeights.current.header + chromeHeights.current.controls,
    );
  };
  const plotRef = useRef<View>(null);
  const plotPageX = useRef(0);
  const widthValue = useSharedValue(0);
  // The viewport: the time window spread across the plot, plus an unsettled pan in points.
  const visibleStart = useSharedValue(rangeStartTime);
  const visibleSpan = useSharedValue(rangeSpan);
  const translation = useSharedValue(0);
  const windowState = useSharedValue<ChartWindowState>({
    count: periodCount,
    span: rangeSpan,
    start: rangeStartTime,
    timeframe,
  });
  const fade = useSharedValue<ChartFade>({
    from: null,
    fromOpacity: 0,
    to: timeframe,
    toOpacity: 1,
  });
  const zoomBlend = useSharedValue(1);
  const pinchStartSpan = useSharedValue(rangeSpan);
  const pinchStartFocal = useSharedValue(0);
  const pinchAnchorTime = useSharedValue(rangeStartTime);
  const axisZoomStart = useSharedValue({ anchorTime: rangeStartTime, focal: 0, span: rangeSpan });
  const panStart = useSharedValue(0);
  const oneFingerPanStart = useSharedValue(0);
  const isOneFingerPanActive = useSharedValue(false);
  // Where and when a one-finger touch began, to recognise a swipe from it.
  const plotTouchStart = useSharedValue({ time: 0, x: 0, y: 0 });
  const axisTouchStart = useSharedValue({ time: 0, x: 0, y: 0 });
  const isPinching = useSharedValue(false);
  const didPinch = useSharedValue(false);
  // Native gestures begin with every touch; only an activated one takes the window over.
  const isPinchActive = useSharedValue(false);
  const isTwoFingerPanActive = useSharedValue(false);
  /** The bar held long enough to start a multi-select, until its press ends. */
  const multiSelectAnchor = useRef<string | null>(null);
  /** A selection being dragged out of a held bar, until its touch ends. */
  const plotSelection = useRef<{ anchorKey: string; lastKey: string } | null>(null);
  /** Windows a released pan already moved the viewport to, until React renders them. */
  const settledPanStarts = useRef<number[]>([]);
  const syncedWindow = useRef<ChartWindowState>({
    count: periodCount,
    span: rangeSpan,
    start: rangeStartTime,
    timeframe,
  });
  const callbacks = useLatest({
    allPeriods,
    loading,
    onPan,
    onSelectSpan,
    onTimeframeChange,
    plotWidth,
    timeframe,
  });
  const changeRange = useCallback(
    (direction: number) => {
      const current = callbacks.current;
      if (!current.loading) {
        const next =
          TIMEFRAMES[Math.max(0, Math.min(2, TIMEFRAMES.indexOf(current.timeframe) + direction))];
        if (next !== undefined && next !== current.timeframe) {
          current.onTimeframeChange(next);
        }
      }
    },
    [callbacks],
  );
  const clearOutgoingLayer = useCallback(
    (outgoing: DeckUpcomingTimeframe) =>
      setLayers((current) =>
        current.outgoing?.timeframe === outgoing ? { ...current, outgoing: null } : current,
      ),
    [],
  );
  const commitPan = useCallback(
    (start: number, count: number) => {
      settledPanStarts.current.push(start);
      callbacks.current.onPan(start, count);
    },
    [callbacks],
  );
  const returnToWindow = useCallback(() => {
    "worklet";
    const window = windowState.get();
    visibleStart.set(withTiming(window.start, ZOOM_TIMING));
    visibleSpan.set(withTiming(window.span, ZOOM_TIMING));
  }, [visibleSpan, visibleStart, windowState]);
  /** Expresses an unsettled pan as time, so a zoom starts from exactly what is on screen. */
  const foldTranslation = useCallback(() => {
    "worklet";
    const width = widthValue.get();
    if (width > 0) {
      visibleStart.set(visibleStart.get() - (translation.get() / width) * visibleSpan.get());
    }
    translation.set(0);
  }, [translation, visibleSpan, visibleStart, widthValue]);
  // Snaps a released pan to whole bars entirely on the UI thread: the viewport moves onto the new
  // window in one update before React hears of it, so the render that follows changes no geometry.
  // Gestures pass the plot width they measured with; it matches `widthValue`.
  const settlePan = useCallback(
    (width: number) => {
      "worklet";
      const window = windowState.get();
      const span = Math.max(1, visibleSpan.get());
      // The instant at the plot's left edge, including the unsettled pan.
      const leftTime = visibleStart.get() - (translation.get() / Math.max(1, width)) * span;
      const buckets =
        width > 0
          ? Math.round((leftTime - window.start) / (window.timeframe === "day" ? HOUR_MS : DAY_MS))
          : 0;
      const start =
        buckets === 0
          ? window.start
          : shiftDeckUpcomingRange(new Date(window.start), window.timeframe, buckets).getTime();
      // A pan keeps the window's bar count (a month view keeps its length), so no bar resizes.
      const end = shiftDeckUpcomingRange(new Date(start), window.timeframe, window.count).getTime();
      if (width > 0) {
        translation.set(translation.get() + ((start - visibleStart.get()) / span) * width);
      }
      visibleStart.set(start);
      translation.set(withSpring(0, PAN_SPRING));
      if (end - start !== visibleSpan.get()) {
        // Daylight-saving days make some windows an hour longer or shorter.
        visibleSpan.set(withTiming(end - start, ZOOM_TIMING));
      }
      if (buckets !== 0) {
        windowState.set({ ...window, span: end - start, start });
        runOnJS(commitPan)(start, window.count);
      }
    },
    [commitPan, translation, visibleSpan, visibleStart, windowState],
  );
  // A zoom crossfades its timeframe layers while the viewport animates to the new window.
  const startZoomTransition = useCallback(
    (from: DeckUpcomingTimeframe, next: ChartWindowState) => {
      "worklet";
      const current = fade.get();
      const blend = zoomBlend.get();
      fade.set({
        from,
        fromOpacity: chartLayerOpacity(from, current, blend),
        to: next.timeframe,
        toOpacity: chartLayerOpacity(next.timeframe, current, blend),
      });
      zoomBlend.set(0);
      zoomBlend.set(
        withTiming(1, ZOOM_TIMING, (finished) => {
          if (finished) {
            runOnJS(clearOutgoingLayer)(from);
          }
        }),
      );
      foldTranslation();
      windowState.set(next);
      visibleStart.set(withTiming(next.start, ZOOM_TIMING));
      visibleSpan.set(withTiming(next.span, ZOOM_TIMING));
    },
    [clearOutgoingLayer, fade, foldTranslation, visibleSpan, visibleStart, windowState, zoomBlend],
  );
  const jumpToWindow = useCallback(
    (next: ChartWindowState) => {
      "worklet";
      windowState.set(next);
      visibleStart.set(next.start);
      visibleSpan.set(next.span);
      translation.set(0);
    },
    [translation, visibleSpan, visibleStart, windowState],
  );
  // React's window for a pan the UI thread settled; adopt its size if it differs from the guess.
  const adoptSettledWindow = useCallback(
    (next: ChartWindowState) => {
      "worklet";
      const window = windowState.get();
      if (
        window.start === next.start &&
        (window.span !== next.span || window.count !== next.count)
      ) {
        windowState.set(next);
        visibleSpan.set(withTiming(next.span, ZOOM_TIMING));
      }
    },
    [visibleSpan, windowState],
  );
  // Windows chosen outside a pan (the header, Today, a zoom, the first render after midnight) move
  // the viewport after React renders them. Their bars are already in place: see `nextChartLayers`.
  useLayoutEffect(() => {
    const previous = syncedWindow.current;
    const next = { count: periodCount, span: rangeSpan, start: rangeStartTime, timeframe };
    syncedWindow.current = next;
    if (previous.timeframe !== timeframe) {
      settledPanStarts.current = [];
      runOnUI(startZoomTransition)(previous.timeframe, next);
      return;
    }
    if (
      previous.start === next.start &&
      previous.span === next.span &&
      previous.count === next.count
    ) {
      return;
    }
    const settled = settledPanStarts.current.indexOf(rangeStartTime);
    if (settled === -1) {
      settledPanStarts.current = [];
      runOnUI(jumpToWindow)(next);
    } else {
      settledPanStarts.current.splice(0, settled + 1);
      runOnUI(adoptSettledWindow)(next);
    }
  }, [
    adoptSettledWindow,
    jumpToWindow,
    periodCount,
    rangeSpan,
    rangeStartTime,
    startZoomTransition,
    timeframe,
  ]);
  const startMultiSelect = useCallback(
    (key: string) => {
      multiSelectAnchor.current = key;
      playMultiSelectFeedback("start");
      callbacks.current.onSelectSpan(key, key);
    },
    [callbacks],
  );
  const endBarPress = useCallback(() => {
    multiSelectAnchor.current = null;
  }, []);
  // One finger: a swipe moves the timeline (the UI-thread pan in `graphGesture`), a tap selects a
  // bar (its Pressable), and holding a bar and then dragging selects every bar between it and the
  // finger. Only that selection drag runs here, on the JS thread that owns the held bar's press.
  const plotSelectionDrag = useMemo(() => {
    const periodKeyAt = (pageX: number) => {
      const current = callbacks.current;
      // Only visible bars join a selection, even after the finger leaves the plot.
      const x = Math.max(0, Math.min(current.plotWidth - 1, pageX - plotPageX.current));
      const time =
        visibleStart.get() +
        ((x - translation.get()) / Math.max(1, current.plotWidth)) * visibleSpan.get();
      const candidates: readonly DeckUpcomingPeriod[] = current.allPeriods;
      const first = candidates[0];
      const period =
        candidates.find(
          (candidate) => candidate.date.getTime() <= time && time < candidate.endDate.getTime(),
        ) ?? (first !== undefined && time < first.date.getTime() ? first : candidates.at(-1));
      return period?.key;
    };
    return PanResponder.create({
      onMoveShouldSetPanResponderCapture: (_event, { numberActiveTouches }) =>
        !callbacks.current.loading &&
        callbacks.current.plotWidth > 0 &&
        numberActiveTouches === 1 &&
        !isPinching.get() &&
        multiSelectAnchor.current !== null,
      onPanResponderGrant: () => {
        // The held bar's press ends once this responder takes over, so keep its key here.
        const anchorKey = multiSelectAnchor.current;
        plotSelection.current = anchorKey === null ? null : { anchorKey, lastKey: anchorKey };
      },
      onPanResponderMove: (event, { numberActiveTouches }) => {
        const selection = plotSelection.current;
        if (selection === null || numberActiveTouches !== 1 || isPinchActive.get()) {
          return;
        }
        const key = periodKeyAt(event.nativeEvent.pageX);
        if (key !== undefined && key !== selection.lastKey) {
          selection.lastKey = key;
          playMultiSelectFeedback("extend");
          callbacks.current.onSelectSpan(selection.anchorKey, key);
        }
      },
      onPanResponderRelease: () => {
        plotSelection.current = null;
      },
      onPanResponderTerminate: () => {
        plotSelection.current = null;
      },
      onPanResponderTerminationRequest: () => false,
      // Bars claim their own touches first; the plot answers the touches between and beneath them.
      onStartShouldSetPanResponder: () => !callbacks.current.loading,
    });
  }, [callbacks, isPinchActive, isPinching, translation, visibleSpan, visibleStart]);
  // The date axis zooms like a pinch anchored where the finger lands: right narrows the timeframe
  // (month, week, day) and left widens it, one step per swipe. It runs on the UI thread, so a busy
  // JS thread (the render after the last swipe) never holds it back.
  const axisZoom = useMemo(() => {
    const scaleFor = (dx: number) => {
      "worklet";
      return clampZoomScale(windowState.get().timeframe, 2 ** (dx / AXIS_ZOOM_DOUBLING_DISTANCE));
    };
    return (
      Gesture.Pan()
        .withTestId("deck-upcoming-chart-axis-zoom")
        .enabled(!loading && plotWidth > 0)
        .maxPointers(1)
        .manualActivation(true)
        .onTouchesDown((event) => {
          const touch = event.allTouches[0];
          if (touch !== undefined) {
            axisTouchStart.set({ time: Date.now(), x: touch.absoluteX, y: touch.absoluteY });
          }
        })
        .onTouchesMove((event, stateManager) => {
          const touch = event.allTouches[0];
          if (touch !== undefined && event.numberOfTouches === 1) {
            const start = axisTouchStart.get();
            if (isHorizontalSwipe(touch.absoluteX - start.x, touch.absoluteY - start.y)) {
              stateManager.activate();
            }
          }
        })
        .onStart((event) => {
          // The swipe zooms around where it began, not where it was recognised.
          const focal = Math.max(
            0,
            Math.min(1, (event.x - event.translationX) / Math.max(1, plotWidth)),
          );
          cancelAnimation(visibleSpan);
          foldTranslation();
          axisZoomStart.set({
            anchorTime: visibleStart.get() + focal * visibleSpan.get(),
            focal,
            span: visibleSpan.get(),
          });
        })
        .onUpdate((event) => {
          const { anchorTime, focal, span } = axisZoomStart.get();
          const nextSpan = span / scaleFor(event.translationX);
          visibleSpan.set(nextSpan);
          visibleStart.set(anchorTime - focal * nextSpan);
        })
        // A cancelled swipe (a second finger, an interruption) only springs back.
        .onEnd((event, success) => {
          const step = success
            ? zoomGestureStep(windowState.get().timeframe, scaleFor(event.translationX))
            : 0;
          if (step === 0) {
            returnToWindow();
          } else {
            runOnJS(changeRange)(step);
          }
        })
    );
  }, [
    axisTouchStart,
    axisZoomStart,
    changeRange,
    foldTranslation,
    loading,
    plotWidth,
    returnToWindow,
    visibleSpan,
    visibleStart,
    windowState,
  ]);
  const graphGesture = useMemo(
    () =>
      Gesture.Simultaneous(
        // One finger moves the timeline on the UI thread, so it follows the finger even while
        // the JS thread renders the window the last swipe settled on.
        Gesture.Pan()
          .withTestId("deck-upcoming-chart-pan")
          .enabled(!loading && plotWidth > 0)
          .maxPointers(1)
          .manualActivation(true)
          .onTouchesDown((event) => {
            const touch = event.allTouches[0];
            if (touch !== undefined) {
              plotTouchStart.set({ time: Date.now(), x: touch.absoluteX, y: touch.absoluteY });
            }
          })
          .onTouchesMove((event, stateManager) => {
            const touch = event.allTouches[0];
            if (touch === undefined || event.numberOfTouches !== 1) {
              return;
            }
            const start = plotTouchStart.get();
            if (!isHorizontalSwipe(touch.absoluteX - start.x, touch.absoluteY - start.y)) {
              return;
            }
            // A bar held this long is a selection being dragged out (its long press), not a pan.
            if (Date.now() - start.time >= MULTI_SELECT_LONG_PRESS_MS) {
              stateManager.fail();
            } else {
              stateManager.activate();
            }
          })
          .onStart(() => {
            isOneFingerPanActive.set(true);
            cancelAnimation(translation);
            oneFingerPanStart.set(translation.get());
          })
          .onUpdate((event) => {
            if (!isTwoFingerPanActive.get() && !isPinchActive.get()) {
              // One adjacent window on either side supplies the bars a pan can reveal.
              translation.set(
                Math.max(
                  -plotWidth,
                  Math.min(plotWidth, oneFingerPanStart.get() + event.translationX),
                ),
              );
            }
          })
          // The release's own travel counts: it can differ from the last update's.
          .onEnd((event) => {
            if (!isTwoFingerPanActive.get() && !isPinchActive.get()) {
              translation.set(
                Math.max(
                  -plotWidth,
                  Math.min(plotWidth, oneFingerPanStart.get() + event.translationX),
                ),
              );
            }
          })
          .onFinalize(() => {
            // A lifted finger and a cancelled pan (a second finger landing) both settle here;
            // an activated two-finger pan or pinch settles the window itself.
            if (isOneFingerPanActive.get() && !isTwoFingerPanActive.get()) {
              if (isPinchActive.get()) {
                translation.set(withSpring(0, PAN_SPRING));
              } else {
                settlePan(plotWidth);
              }
            }
            isOneFingerPanActive.set(false);
          }),
        Gesture.Pinch()
          .enabled(!loading)
          .onTouchesDown((event) => {
            if (event.numberOfTouches <= 2) {
              didPinch.set(false);
            }
          })
          .onStart((event) => {
            isPinchActive.set(true);
            cancelAnimation(visibleSpan);
            cancelAnimation(visibleStart);
            pinchStartSpan.set(visibleSpan.get());
            const focal = Math.max(0, Math.min(1, event.focalX / Math.max(1, widthValue.get())));
            // Include an unfinished pan so the date under the fingers never jumps on activation.
            pinchStartFocal.set(focal - translation.get() / Math.max(1, widthValue.get()));
            pinchAnchorTime.set(visibleStart.get() + pinchStartFocal.get() * visibleSpan.get());
          })
          .onUpdate((event) => {
            if (!isPinching.get() && (event.scale < 0.92 || event.scale > 1.08)) {
              isPinching.set(true);
              didPinch.set(true);
              cancelAnimation(translation);
              translation.set(0);
            }
            const span =
              pinchStartSpan.get() / clampZoomScale(windowState.get().timeframe, event.scale);
            const focal = isPinching.get()
              ? Math.max(0, Math.min(1, event.focalX / Math.max(1, widthValue.get())))
              : pinchStartFocal.get();
            visibleSpan.set(span);
            visibleStart.set(pinchAnchorTime.get() - focal * span);
          })
          .onEnd((event) => {
            const step = zoomGestureStep(windowState.get().timeframe, event.scale);
            if (step === 0) {
              returnToWindow();
            } else {
              runOnJS(changeRange)(step);
            }
          })
          .onFinalize((_event, success) => {
            isPinching.set(false);
            if (!success && isPinchActive.get()) {
              returnToWindow();
            }
            isPinchActive.set(false);
          }),
        Gesture.Pan()
          .enabled(!loading)
          .minPointers(2)
          .maxPointers(2)
          .averageTouches(true)
          .minDistance(8)
          .onStart(() => {
            isTwoFingerPanActive.set(true);
            cancelAnimation(translation);
            panStart.set(translation.get());
          })
          .onUpdate((event) => {
            if (!isPinching.get()) {
              translation.set(
                Math.max(-plotWidth, Math.min(plotWidth, panStart.get() + event.translationX)),
              );
            }
          })
          .onEnd((event) => {
            if (!didPinch.get()) {
              translation.set(
                Math.max(-plotWidth, Math.min(plotWidth, panStart.get() + event.translationX)),
              );
              settlePan(plotWidth);
            }
          })
          .onFinalize((_event, success) => {
            // A one-finger pan also begins this gesture, which then fails without moving anything.
            if (!success && isTwoFingerPanActive.get()) {
              translation.set(withSpring(0, PAN_SPRING));
            }
            isTwoFingerPanActive.set(false);
          }),
      ),
    [
      changeRange,
      didPinch,
      isOneFingerPanActive,
      isPinchActive,
      isPinching,
      isTwoFingerPanActive,
      loading,
      oneFingerPanStart,
      panStart,
      pinchAnchorTime,
      pinchStartFocal,
      pinchStartSpan,
      plotTouchStart,
      plotWidth,
      returnToWindow,
      settlePan,
      translation,
      visibleSpan,
      visibleStart,
      widthValue,
      windowState,
    ],
  );
  const extraGroups = [
    {
      count: timeframe === "day" || selectedKey === "overdue" ? overdueCount : 0,
      key: "overdue",
      label: "Overdue",
    },
    { count: unscheduledCount, key: "unscheduled", label: "Unscheduled" },
  ];
  const layerProps = {
    fade,
    loading,
    maximumCardCount,
    onSelect,
    origin: trackOrigin,
    selectedKey,
    selectedSpan,
    todayKey,
    translation,
    visibleSpan,
    visibleStart,
    widthValue,
    zoomBlend,
  };
  // Layers are keyed by timeframe, so a zoom mounts the new timeframe's layer (invisible until its
  // fade begins) and keeps the one being left, whose viewport and fade never restart.
  const layerWindows = [layers.outgoing, layers.current].flatMap((window, index) =>
    window === null ? [] : [{ outgoing: index === 0, window }],
  );
  return (
    <View className="min-h-0 flex-1 px-[9px] pb-1" testID="deck-upcoming-review-chart">
      <Surface className="min-h-0 flex-1">
        <SurfaceHeader
          className="flex-row items-center gap-2 py-1 pr-1"
          onLayout={({ nativeEvent: { layout } }) => reportChromeHeight("header", layout.height)}
        >
          <Text
            accessibilityActions={[
              { label: "Show a wider range", name: "increment" },
              { label: "Show a narrower range", name: "decrement" },
            ]}
            accessibilityHint="Adjust to zoom between day, week, and month."
            accessibilityRole="adjustable"
            accessibilityValue={{
              max: 2,
              min: 0,
              now: TIMEFRAMES.indexOf(timeframe),
              text: timeframe,
            }}
            className="min-w-0 flex-1 text-sm font-semibold"
            numberOfLines={1}
            onAccessibilityAction={(event) =>
              changeRange(event.nativeEvent.actionName === "increment" ? 1 : -1)
            }
            testID="deck-upcoming-chart-range"
          >
            {rangeLabel}
          </Text>
          <PeriodNavigationControls
            disabled={loading}
            isTodayInView={isTodayInView}
            nextLabel={`Next ${timeframe}`}
            onNext={() => onNavigate(1)}
            onPrevious={() => onNavigate(-1)}
            onToday={onToday}
            previousLabel={`Previous ${timeframe}`}
            testID="deck-upcoming-chart-navigation"
            todayHint="Moves the chart back to today."
          />
        </SurfaceHeader>
        <View className="min-h-0 flex-1 px-3 pt-3">
          <GestureDetector gesture={graphGesture}>
            <View
              {...plotSelectionDrag.panHandlers}
              accessibilityHint="Tap a bar to select its reviews, or touch and hold a bar and then drag to select several. Swipe to move the timeline. Pinch to zoom."
              className="min-h-0 flex-1 overflow-hidden border-b border-border"
              onLayout={({ nativeEvent: { layout } }) => {
                setPlotWidth(layout.width);
                widthValue.set(layout.width);
                plotRef.current?.measureInWindow((x) => {
                  plotPageX.current = x;
                });
              }}
              ref={plotRef}
              testID="deck-upcoming-chart-bars"
            >
              {layerWindows.map(({ outgoing, window }) => (
                <DeckUpcomingChartLayer
                  {...layerProps}
                  chartWindow={window}
                  key={window.timeframe}
                  onBarHold={startMultiSelect}
                  onBarPressOut={endBarPress}
                  outgoing={outgoing}
                  retainedPeriods={outgoing ? null : layers.retained}
                />
              ))}
            </View>
          </GestureDetector>
          <View
            className="gap-2 py-2"
            onLayout={({ nativeEvent: { layout } }) =>
              reportChromeHeight("controls", layout.height)
            }
            testID="deck-upcoming-chart-controls"
          >
            {/* The date axis doubles as a zoom control: swiping right zooms in, left zooms out. */}
            <GestureDetector gesture={axisZoom}>
              <View
                accessibilityHint="Swipe right to zoom in or left to zoom out."
                className={timeframe === "day" ? "h-4 overflow-hidden" : "h-9 overflow-hidden"}
                testID="deck-upcoming-chart-timeline"
              >
                {layerWindows.map(({ outgoing, window }) => (
                  <DeckUpcomingChartLayer
                    {...layerProps}
                    axis
                    chartWindow={window}
                    key={window.timeframe}
                    outgoing={outgoing}
                    retainedPeriods={outgoing ? null : layers.retained}
                  />
                ))}
              </View>
            </GestureDetector>
            {extraGroups.some((group) => group.count > 0) ? (
              <View className="flex-row flex-wrap gap-2">
                {extraGroups
                  .filter((group) => group.count > 0)
                  .map((group) => (
                    <Pressable
                      accessibilityLabel={`${group.label}, ${group.count} ${group.count === 1 ? "card" : "cards"}`}
                      accessibilityRole="button"
                      accessibilityState={{ selected: selectedKey === group.key }}
                      className={cn(
                        "rounded-full px-3 py-1",
                        selectedKey === group.key ? "bg-foreground" : "bg-surface",
                      )}
                      disabled={loading}
                      key={group.key}
                      onPress={() => onSelect(group.key, true)}
                      testID={`deck-upcoming-${group.key}`}
                    >
                      <Text
                        className={cn(
                          "text-xs",
                          selectedKey === group.key ? "text-background" : "text-muted-foreground",
                        )}
                      >
                        {group.label} · {group.count}
                      </Text>
                    </Pressable>
                  ))}
              </View>
            ) : null}
          </View>
        </View>
      </Surface>
    </View>
  );
}

function DeckUpcomingChartLayer({
  axis = false,
  chartWindow,
  fade,
  loading,
  maximumCardCount,
  onBarHold,
  onBarPressOut,
  onSelect,
  origin,
  outgoing,
  retainedPeriods,
  selectedKey,
  selectedSpan,
  todayKey,
  translation,
  visibleSpan,
  visibleStart,
  widthValue,
  zoomBlend,
}: {
  axis?: boolean;
  chartWindow: ChartWindow;
  fade: SharedValue<ChartFade>;
  loading: boolean;
  maximumCardCount: number;
  onBarHold?: (key: string) => void;
  onBarPressOut?: () => void;
  onSelect: (key: string, toggle?: boolean) => void;
  /** The instant at the track's left edge; constant for the chart's lifetime. */
  origin: number;
  outgoing: boolean;
  /** Bars of a window jumped away from, drawn until the viewport leaves them. */
  retainedPeriods: readonly DeckUpcomingPeriod[] | null;
  selectedKey: string;
  selectedSpan: SelectedSpan | undefined;
  todayKey: string;
  translation: SharedValue<number>;
  visibleSpan: SharedValue<number>;
  visibleStart: SharedValue<number>;
  widthValue: SharedValue<number>;
  zoomBlend: SharedValue<number>;
}) {
  const { adjacentPeriods, periods, timeframe } = chartWindow;
  // Constant per layer (layers are keyed by timeframe), so the track's mapper never restarts.
  const layer = timeframe;
  const trackStyle = useAnimatedStyle(() => {
    const pixelsPerMs = widthValue.get() / Math.max(1, visibleSpan.get());
    return {
      opacity: chartLayerOpacity(layer, fade.get(), zoomBlend.get()),
      transform: [{ translateX: (origin - visibleStart.get()) * pixelsPerMs + translation.get() }],
      width: TRACK_UNIT_MS * pixelsPerMs,
    };
  });
  const windowPeriods = chartWindowPeriods(chartWindow);
  const windowKeys = new Set(windowPeriods.map((period) => period.key));
  const visibleKeys = new Set(periods.map((period) => period.key));
  const maximum = Math.max(1, maximumCardCount);
  const placement = (from: Date, to: Date) => ({
    left: trackPercent(from.getTime() - origin),
    width: trackPercent(to.getTime() - from.getTime()),
  });
  const axisGroups = [
    ...[adjacentPeriods?.before, periods, adjacentPeriods?.after].map((group, index) => ({
      isVisible: !outgoing && index === 1,
      periods: group ?? [],
    })),
    { isVisible: false, periods: retainedPeriods ?? [] },
  ].filter((group) => group.periods.length > 0);
  return (
    <Animated.View
      accessibilityElementsHidden={outgoing}
      className="absolute inset-y-0 left-0"
      importantForAccessibility={outgoing ? "no-hide-descendants" : "auto"}
      pointerEvents={outgoing || axis ? "none" : "auto"}
      style={trackStyle}
    >
      {axis
        ? axisGroups.map((group) => {
            const first = group.periods[0];
            const last = group.periods.at(-1);
            return first === undefined || last === undefined ? null : (
              <View
                className="absolute inset-y-0"
                key={`${first.key}~${last.key}`}
                style={placement(first.date, last.endDate)}
              >
                <DeckUpcomingChartAxis
                  isVisible={group.isVisible}
                  periods={group.periods}
                  selectedSpan={selectedSpan}
                  timeframe={timeframe}
                />
              </View>
            );
          })
        : [
            ...windowPeriods,
            ...(retainedPeriods ?? []).filter((period) => !windowKeys.has(period.key)),
          ].map((period) => {
            const selected = isDeckUpcomingPeriodSelected(period, selectedSpan);
            const isVisible = !outgoing && visibleKeys.has(period.key);
            const accessibleDate =
              period.hour === undefined && period.key === todayKey
                ? "Today"
                : period.date.toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  });
            const accessiblePeriod =
              period.hour === undefined
                ? accessibleDate
                : `${accessibleDate}, ${deckUpcomingHourLabel(period)}`;
            const stages = USER_CARD_STAGE_ORDER.filter((stage) => period.stageCounts[stage] > 0);
            const accessibleStages = stages
              .map((stage) => `${period.stageCounts[stage]} ${USER_CARD_STAGE_LABEL[stage]}`)
              .join(", ");
            return (
              <LongPressable
                accessibilityElementsHidden={!isVisible}
                accessibilityHint={
                  period.key === selectedKey ? "Tap again to show all visible reviews." : undefined
                }
                accessibilityLabel={
                  loading
                    ? `Loading reviews for ${accessiblePeriod}`
                    : `${accessiblePeriod}, ${period.cardCount} ${period.cardCount === 1 ? "review" : "reviews"} ${localDayKey(period.date) < todayKey ? "completed" : "due"}${accessibleStages === "" ? "" : `: ${accessibleStages}`}`
                }
                accessibilityRole="button"
                accessibilityState={{ selected }}
                className={cn(
                  "absolute inset-y-0 justify-end",
                  timeframe === "week" ? "px-2" : "px-0.5",
                )}
                delayLongPress={MULTI_SELECT_LONG_PRESS_MS}
                disabled={loading || outgoing}
                importantForAccessibility={isVisible ? "auto" : "no-hide-descendants"}
                key={period.key}
                onLongPress={onBarHold === undefined ? undefined : () => onBarHold(period.key)}
                onPress={() => onSelect(period.key, true)}
                onPressOut={onBarPressOut}
                // Only data-derived geometry is inline; static styling stays in Uniwind.
                style={placement(period.date, period.endDate)}
                testID={
                  outgoing
                    ? undefined
                    : `deck-upcoming-${period.hour === undefined ? "day" : "hour"}-${period.key}`
                }
              >
                {selected ? (
                  <View className="absolute inset-0 rounded-t-xs bg-foreground/10" />
                ) : null}
                {/* Stage segments stack from New at the baseline, like the stats charts. */}
                <View
                  className={cn(
                    "flex-col-reverse overflow-hidden rounded-t-xs",
                    period.cardCount === 0 ? "h-0.5 bg-border" : "min-h-1",
                  )}
                  style={
                    period.cardCount === 0
                      ? undefined
                      : { height: `${(period.cardCount / maximum) * 100}%` }
                  }
                >
                  {stages.map((stage) => (
                    <View
                      className={STAGE_BAR_SEGMENT_CLASS_NAME[stage]}
                      key={stage}
                      style={{ flex: period.stageCounts[stage] }}
                    />
                  ))}
                </View>
              </LongPressable>
            );
          })}
    </Animated.View>
  );
}

function DeckUpcomingChartAxis({
  isVisible,
  periods,
  selectedSpan,
  timeframe,
}: {
  isVisible: boolean;
  periods: readonly DeckUpcomingPeriod[];
  selectedSpan: SelectedSpan | undefined;
  timeframe: DeckUpcomingTimeframe;
}) {
  const start = periods[0]?.date;
  const end = periods.at(-1)?.endDate;
  const isSelected = periods.map((period) => isDeckUpcomingPeriodSelected(period, selectedSpan));
  const firstSelected = isSelected.indexOf(true);
  const lastSelected = isSelected.lastIndexOf(true);
  // A selection labels its first bar, and its last too when the two labels have room.
  const selectedMarks =
    firstSelected === -1
      ? []
      : lastSelected - firstSelected >= (timeframe === "month" ? 5 : 2)
        ? [firstSelected, lastSelected]
        : [firstSelected];
  const isClearOfSelection = (index: number, distance: number) =>
    selectedMarks.every((mark) => Math.abs(index - mark) >= distance);
  const hourTicks = [
    ...[0, 6, 12, 18, 24].filter(
      (tick) => selectedMarks.includes(tick) || isClearOfSelection(tick, 2),
    ),
    ...selectedMarks.filter((mark) => mark % 6 !== 0),
  ];
  return (
    <View
      accessibilityElementsHidden={!isVisible}
      className="relative min-w-0 flex-1 flex-row"
      importantForAccessibility={isVisible ? "auto" : "no-hide-descendants"}
      testID={isVisible ? "deck-upcoming-chart-axis" : undefined}
    >
      {timeframe === "day" && start !== undefined && end !== undefined
        ? hourTicks.map((tick) => (
            <Text
              className={cn(
                "absolute top-0 text-center text-[10px] text-muted-foreground tabular-nums",
                isSelected[tick] === true && "font-bold text-foreground",
              )}
              key={tick}
              style={{
                left: `${(tick / 24) * 100}%`,
                transform: [{ translateX: tick === 0 ? 0 : tick === 24 ? -16 : -8 }],
              }}
            >
              {tick === 24 && start.getHours() === 0 && end.getHours() === 0
                ? 24
                : new Date(start.getTime() + tick * 60 * 60 * 1000).getHours()}
            </Text>
          ))
        : periods.map((period, index) => (
            <View
              className="min-w-0 items-center"
              key={period.key}
              // Columns match the bars, which are as wide as their days (daylight saving included).
              style={{ flex: (period.endDate.getTime() - period.date.getTime()) / HOUR_MS }}
            >
              {timeframe === "week" ||
              selectedMarks.includes(index) ||
              (isClearOfSelection(index, 5) &&
                [
                  0,
                  Math.floor(periods.length / 4),
                  Math.floor(periods.length / 2),
                  Math.floor((periods.length * 3) / 4),
                  periods.length - 1,
                ].includes(index)) ? (
                <View
                  className={cn(
                    "items-center",
                    timeframe === "month" && "absolute top-0 w-14",
                    timeframe === "month" && index < 3 && "left-0 items-start",
                    timeframe === "month" && index >= periods.length - 3 && "right-0 items-end",
                  )}
                  testID={isVisible ? `deck-review-label-${period.key}` : undefined}
                >
                  <Text
                    className={cn(
                      "text-xs text-muted-foreground tabular-nums",
                      isSelected[index] === true && "font-bold text-foreground",
                    )}
                    numberOfLines={1}
                  >
                    {period.date.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                  </Text>
                  <Text
                    className={cn(
                      "text-[10px] text-muted-foreground",
                      isSelected[index] === true && "font-bold text-foreground",
                    )}
                    numberOfLines={1}
                  >
                    {period.date.toLocaleDateString(undefined, { weekday: "short" })}
                  </Text>
                </View>
              ) : null}
            </View>
          ))}
    </View>
  );
}
